import { Compass, Download, Feather, FileText, Heart, Moon } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";

const VALUES = [
  { icon: Heart, title: "Story First", text: "Every song begins with a real story — yours. Lyrics are never templated; they're written around the people and moments that matter." },
  { icon: Compass, title: "Craft & Direction", text: "From first consultation to final master, each track is guided with intent — musical directions previewed, chosen and refined together." },
  { icon: Feather, title: "Independent Spirit", text: "A truly independent label. No committees, no formulas — just honest, cinematic songwriting and production." },
  { icon: Moon, title: "Beyond The Horizon", text: "Music written where the sea meets the sky — made to carry memory, hope and everything in between." },
];

const BIO = [
  {
    h: "Real Life. Real Stories. Real Songs.",
    p: [
      "I was born in Lancashire, and music has been part of my life for as long as I can remember. My love affair with music really began in the 1970s. It was a different world back then. No internet, no mobile phones, no streaming services and certainly no TikTok. Sunday evenings meant one thing: listening to the Top 40, cassette recorder at the ready, hoping to capture my favourite songs without the DJ talking over the ending!",
      "Those were wonderful times. Music was something you looked forward to, something you collected, something you felt. And that love of melody, rhythm and the emotions carried within a song has never left me.",
      "For 35 years, I worked in retail, meeting people from all walks of life. You learn a great deal about human nature over that length of time. You hear stories, witness happiness and heartache, and begin to understand that everyone carries experiences the rest of the world may never see.",
      "My own life has certainly had its share. Some of those challenges began when I was just three months old. Others came much later. There have been chapters that brought happiness, chapters that changed everything, and chapters I would not wish to live through again. But every experience has helped shape the person I am today.",
    ],
  },
  {
    h: "When Words Became Songs",
    p: [
      "In 2010, my life changed suddenly and devastatingly when I lost my wife to a brain haemorrhage. There are moments in life for which nothing can prepare you. Finding a way to express what I was feeling became part of my journey through that loss.",
      "I had written poetry, with some of my work published online, but gradually I discovered that words could carry even more meaning when combined with music. The song Lead Me Home tells part of that story.",
      "Around 2013, another moment left a lasting impression on me. While visiting Lake Geneva, I came across Melancholy, the sculpture by Albert György. I sat with it for nearly two hours. Something about that sculpture spoke to me in a way I still find difficult to explain. It seemed to capture feelings that words alone could not fully express. That experience became part of the inspiration behind Hearts Echo.",
      "Other songs have emerged from different chapters of my life. Unknowing Goodbye reflects the breakdown of my second marriage after 13 years, while The Best Is Yet to Come looks towards the difficulties I have experienced since the very beginning of my life, and the determination to keep moving forward.",
      "These are not stories I have invented to make songs. They are pieces of my life.",
    ],
  },
  {
    h: "A Lifetime of Stories, Finally Finding Their Voice",
    p: [
      "When I semi-retired, I found myself with the opportunity to do something I had wanted to do for years: turn my writing into finished music. What began with poems, thoughts and lyrics has grown into a collection of more than 70 tracks in various stages of development.",
      "I play the synthesizer and have a lifelong love of music, melody and rhythm. Bringing those influences together with my writing has opened up a whole new chapter.",
      "I do not keep a journal. I never have. In many ways, my songs have become my way of recording the moments, people and experiences that have shaped my life. People who know some of what I have been through occasionally wonder how I am still standing, let alone creating music. The answer, perhaps, is that we all find our own way of carrying on.",
      "Some people write books. Some paint pictures. Some tell their stories in conversation. I could not put my life into a book, so I found a way to put it into songs. And through those songs, I hope others might recognise something of their own journey too.",
    ],
  },
  {
    h: "Your Story Matters Too",
    p: [
      "Creating Open Horizon Records has also given me the opportunity to help other people tell their stories. After a lifetime of meeting people, listening to their experiences and living through plenty of my own, I understand that the most meaningful memories are not always the grand occasions.",
      "Sometimes they are the little things. A familiar phrase. A shared joke. A place. A promise. Someone who changed your life without ever realising it.",
      "Through our Bespoke Music service, I work with people to turn those memories and experiences into original songs, shaped around their preferred musical style and the emotions they want to capture. Whether it is a celebration, a tribute, a thank-you or a message to someone you love, the aim is always the same: to create something personal, meaningful and lasting.",
    ],
  },
  {
    h: "The Journey Continues",
    p: [
      "Open Horizon Records is not the end of my story. It is another beginning. There are still songs to finish, memories to explore and stories waiting to be told.",
      "If you have found your way here, I hope you will take a little time to listen. You might hear something of my life in the music. And perhaps, somewhere along the way, you will hear something of your own.",
    ],
  },
];

export default function About() {
  return (
    <div className="pt-28 pb-0" data-testid="about-page">
      {/* ABOUT MIKE — leads the page */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="about-founder">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <Reveal className="lg:col-span-2 lg:sticky lg:top-28">
            <div className="relative">
              <div className="absolute -inset-6 bg-gold-400/10 blur-3xl rounded-full" aria-hidden />
              <img
                src="/assets/mike-profile.webp"
                alt="Mike, founder of Open Horizon Records, writing lyrics in his studio"
                className="relative w-full h-auto rounded-2xl border border-gold-400/25 shadow-[0_30px_90px_rgba(0,0,0,0.65)]"
                data-testid="founder-photo"
              />
              <p className="mt-4 text-xs tracking-[0.25em] uppercase text-slate-500 text-center">
                Mike — Founder & Songwriter
              </p>
            </div>
          </Reveal>
          <div className="lg:col-span-3">
            <Reveal>
              <p className="eyebrow mb-3">About Me</p>
              <h1 className="font-serif text-3xl sm:text-5xl text-slate-50 leading-tight">
                The Story Behind <span className="text-gold-gradient">Open Horizon Records</span>
              </h1>
            </Reveal>
            {BIO.map((s, i) => (
              <Reveal key={s.h} delay={0.05} className="mt-10">
                <h3 className="font-serif text-xl sm:text-2xl text-gold-200">{s.h}</h3>
                {s.p.map((para) => (
                  <p key={para.slice(0, 24)} className="mt-4 text-slate-300/85 font-light leading-relaxed text-[15px]">
                    {para}
                  </p>
                ))}
              </Reveal>
            ))}
            <Reveal className="mt-12">
              <blockquote className="border-l-2 border-gold-400/60 pl-6">
                <p className="font-serif text-xl sm:text-2xl text-gold-200 leading-relaxed">
                  "Whatever life brings, there's always something beyond the horizon."
                </p>
                <footer className="mt-4 text-sm text-slate-400">
                  Have the best life, <span className="text-gold-300 font-serif">Mike</span>
                </footer>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </div>

      {/* THE LABEL */}
      <div className="mt-24">
        <Marquee items={["Every Story Deserves Its Own Song", "Independent", "Cinematic", "Handcrafted", "Music Beyond The Horizon"]} />
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="eyebrow mb-3">The Label</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-slate-50 leading-tight">
              Music written where the <span className="text-gold-gradient">sea meets the sky</span>
            </h2>
            <p className="mt-7 text-slate-300/85 font-light leading-relaxed">
              Open Horizon Records is an independent label built on a simple belief: every story
              deserves its own song. Founded by songwriter Mike — after a lifetime of real stories
              finally found their voice — the label turns lived experience into cinematic,
              story-driven music.
            </p>
            <p className="mt-5 text-slate-300/85 font-light leading-relaxed">
              Alongside our releases, our Bespoke Music service writes completely original songs
              for life's biggest moments — weddings, anniversaries, birthdays, memorials and
              tributes — crafted with you, from your story, and produced to studio standard.
            </p>
            <Link to="/bespoke" className="btn-gold mt-9" data-testid="about-bespoke-cta">
              Commission a Song
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-8 bg-gold-400/10 blur-3xl rounded-full" aria-hidden />
              <img
                src="/assets/about-logo.webp"
                alt="Open Horizon Records — golden horizon emblem"
                className="relative w-full h-auto rounded-2xl border border-gold-400/20 shadow-[0_30px_90px_rgba(0,0,0,0.65)]"
                data-testid="about-artwork"
              />
            </div>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal className="text-center">
          <p className="eyebrow mb-3">What Guides Us</p>
          <h2 className="font-serif text-3xl sm:text-5xl text-slate-50">Our Values</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div className="rounded-2xl border border-white/10 bg-navy-900/60 p-7 h-full hover:border-gold-400/40 transition-colors duration-500" data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <v.icon size={26} className="text-gold-400" />
                <h3 className="font-serif text-xl text-slate-100 mt-4">{v.title}</h3>
                <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-20">
          <div className="relative rounded-3xl overflow-hidden border border-gold-400/20">
            <img src="/assets/hero-moonlit.webp" alt="Moonlit horizon over water" className="w-full h-72 sm:h-96 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 to-transparent flex items-end">
              <p className="font-serif text-2xl sm:text-3xl text-gold-200 p-8 sm:p-12">
                "Bring us your story. Let's give it a sound."
              </p>
            </div>
          </div>
        </Reveal>

        {/* PRESS PACK */}
        <Reveal className="mt-20 mb-4">
          <div className="rounded-3xl border border-gold-400/25 bg-navy-900/70 backdrop-blur-md p-8 sm:p-12 text-center" data-testid="about-press-pack">
            <p className="eyebrow mb-3">Press Pack</p>
            <h2 className="font-serif text-2xl sm:text-4xl text-slate-50">Media Downloads</h2>
            <p className="mt-4 text-sm text-slate-400 font-light max-w-xl mx-auto leading-relaxed">
              Everything a radio presenter, playlist curator or journalist needs — the complete kit
              with logos and artwork, the press biography, and the full story behind the label.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a href="/assets/Open-Horizon-Press-Kit.zip" download className="btn-gold !px-6 !py-3 text-xs" data-testid="about-presskit-download">
                <Download size={15} /> Complete Press Kit (ZIP)
              </a>
              <a href="/assets/Open-Horizon-Press-Bio.pdf" download className="btn-ghost-gold !px-6 !py-3 text-xs" data-testid="about-pressbio-download">
                <FileText size={15} /> Press Biography (PDF)
              </a>
              <a href="/assets/Open-Horizon-About-Me.pdf" download className="btn-ghost-gold !px-6 !py-3 text-xs" data-testid="about-me-pdf-download">
                <FileText size={15} /> About Me — The Full Story (PDF)
              </a>
            </div>
            <p className="mt-6 text-xs text-slate-500">
              Radio & playlist enquiries: openhorizonrecords@outlook.com · Full details on the{" "}
              <Link to="/press" className="text-gold-300 hover:underline">Press &amp; Media page</Link>
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
